<template>
  <transition name="modal">
    <div
      v-if="showing"
      class="v-modal min-h-screen inset-0"
      role="dialog"
      aria-modal="true"
      :class="{ 'overflow-hidden': !overflowContent, 'mobile-fullscreen': mobileFullscreen, fixed: !confined }"
    >
      <div class="v-modal__mask flex items-center justify-center min-h-screen text-center">
        <div
          class="v-modal__container bg-white text-left shadow-xl transform transition-all relative sm_align-middle"
          :class="[containerClasses, { 'overflow-hidden': !overflowContent }]"
        >
          <div class="flex flex-row-reverse sm_bg-gray-100 rounded-tl-lg rounded-tr-lg">
            <slot name="closeBtn">
              <button
                v-if="hideClose === false && showHeader"
                class="p-0 m-4 mt-5 sm_m-6 bg-transparent rounded-md text-gray-900 z-10 hover_text-gray-600 sm_top-6 sm_right-6 self-start align-center focus_outline-none"
                type="button"
                data-testid="modal-close-btn"
                @click="$emit('update:showing', false)"
              >
                <span class="sr-only">Close</span>
                <XMarkIcon class="w-6" />
              </button>
            </slot>
            <header v-if="showHeader" class="flex-grow p-4 pt-5 sm_p-6 min-w-0">
              <slot name="header" />
            </header>
          </div>
          <div
            v-if="showContent"
            class="v-modal__content p-4 pt-5 sm_p-6"
            :style="{ maxHeight: maxContent }"
            :class="{ 'overflow-y-auto': !overflowContent }"
          >
            <button
              v-if="hideClose === false && !showHeader"
              class="p-0 bg-transparent rounded-md text-gray-900 z-10 hover_text-gray-600 sm_top-6 sm_right-6 absolute right-4 focus_outline-none"
              type="button"
              data-testid="modal-close-btn"
              @click="$emit('update:showing', false)"
            >
              <span class="sr-only">Close</span>
              <XMarkIcon class="w-6" />
            </button>
            <slot name="default" />
          </div>
          <footer
            v-if="showFooter"
            class="p-4 pt-5 sm_p-6"
            :class="{ 'bg-gray-100': !borderedFooter, 'border-gray-200 border-t': borderedFooter }"
          >
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </div>
  </transition>
</template>
<script lang="ts" setup>
import { XMarkIcon } from '@heroicons/vue/24/solid'

const props = defineProps({
  showing: {
    type: Boolean,
    default: false,
  },
  mobileFullscreen: {
    type: [Boolean, String],
    default: false,
  },
  hideClose: {
    type: [Boolean, String],
    default: false,
  },
  containerClasses: {
    type: String,
    default: '',
  },
  maxContent: {
    type: String,
    default: 'none',
  },
  overflowContent: {
    type: [Boolean, String],
    default: false,
  },
  borderedFooter: {
    type: [Boolean, String],
    default: false,
  },
  confined: {
    type: [Boolean, String],
    default: false,
  },
})

const emit = defineEmits(['update:showing'])

const slots = useSlots()

usePreventBodyScroll({ shouldStopScroll: toRef(props, 'showing') })

const showHeader = computed(() => slots.header?.())
const showContent = computed(() => slots.default?.())
const showFooter = computed(() => slots.footer?.())

const handleEscapeClose = (e: KeyboardEvent) => {
  if (props.showing && e.key === 'Escape') {
    emit('update:showing', false)
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleEscapeClose)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscapeClose)
})
</script>

<style lang="stylus" scoped>
/* See assets/css/tailwind.css for default (overrideable) styles. */

.modal-enter-active
  @apply ease-out duration-300

.modal-leave-active
  @apply ease-in duration-200

  .v-modal__mask
    @apply duration-300

.modal-enter .v-modal__mask,
.modal-leave-to .v-modal__mask
  @apply opacity-0

.v-modal.no-padding
  header, footer, .v-modal__content
    @apply p-0 pt-0 sm_p-0

.modal-enter .v-modal__container,
.modal-leave-to .v-modal__container
  @apply opacity-0 translate-y-4 sm_translate-y-0 sm_scale-95

.v-modal:not(.mobile-fullscreen)
  .v-modal__mask
    @apply p-4
  .v-modal__container
    @apply rounded-lg

.v-modal.mobile-fullscreen .v-modal__container
  @apply rounded-none w-screen h-screen

@screen sm
  .v-modal.mobile-fullscreen .v-modal__container
    @apply rounded-lg w-auto h-auto
</style>
